import React, { useEffect, useState } from 'react';
import useAolSaveHandler from '../../../app/useAolSaveHandler';
import { useAolStore } from '../../../context/AolContext';
import { NoonReportGeneralSectionFactory } from '../../../factory/ValidatedNoonReportFactory';
import { NoonReportSectionEnum, NoonReportTypeEnum } from '../../../types/ViewModel/AolEnums';
import { NoonReportSectionData } from '../../../types/ViewModel/NoonReportSectionData';
import { createSectionData, getNumberValue, hasInvalidFields, hasMissingValues } from '../../../utils/aolFieldsHelper';
import OdfjellInputComponent, { InputChangeData } from '../../common/odfjell-input-component/OdfjellInputComponent';
import { useVesselStore } from '../../../context/VesselContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GeneralSectionInputElementWrapper } from './GeneralSection.style';
import { roundTo0Decimal, roundTo2Decimals, truncateTo2Decimals } from '../../../utils/numberHelper';
import { AolDataItem } from '../../../types/ViewModel/AolItem';
import { ValidatedNoonReport } from '../../../types/ViewModel/ValidatedNoonReport';
import { NoonReportDateTime } from '../../../types/ViewModel/NoonReportDateTime';
import useReadOnlyUtils from 'src/utils/useReadOnlyUtils';

type VM = {
    cargoOnBoard?: number;
    ballast?: number;
    hours?: number;
    idlOffsetHours?: number;
    manualOffsetHours?: number;

    sectionData?: NoonReportSectionData;
    notValidFieldsArray?: string[];
    hasInvalidFields?: boolean;

    targetConsumption?: number;
    targetSpeed?: number;
    targetDeviation?: string;
    targetDeviationDetails?: string[];
    isTargetDeviationWarning?: boolean;
    noonReportDateTime?: NoonReportDateTime;

    mrvSectionWarning?: boolean;
    isInitialized?: boolean;
};

const GeneralSectionComponent: React.FC = () => {
    const { vessel } = useVesselStore();
    const [generalSectionVM, setGeneralSectionVM] = useState<VM>({});
    const [hasEmptyValues, setHasEmptyValues] = useState<boolean>(false);
    const { saveAolEvent, saveSection } = useAolSaveHandler();
    const { selectedAolDataItem, aolSyncEvent } = useAolStore();
    const [targetDeviationDetailsExpanded, setTargetDeviationDetailsExpanded] = useState(false);
    const { checkIsAolReadOnly } = useReadOnlyUtils();

    useEffect(() => {
        if (!selectedAolDataItem) return;

        if (!generalSectionVM.isInitialized) {
            initVM();
        } else {
            updateTargetDeviationInVm(generalSectionVM);
        }
    }, [selectedAolDataItem, vessel]);

    useEffect(() => {
        if (
            saveAolEvent &&
            saveAolEvent.section === NoonReportSectionEnum.General &&
            selectedAolDataItem &&
            saveAolEvent.aolDataItem.id === selectedAolDataItem.id
        ) {
            if (saveAolEvent.type === 'save_section') save();
            if (saveAolEvent.type === 'save_section_done') initVM();
        }
    }, [saveAolEvent]);

    useEffect(() => {
        if (aolSyncEvent && aolSyncEvent.noonReport && aolSyncEvent.type === 'sync_hours') {
            initVM();
        }
    }, [aolSyncEvent]);

    const isReadOnly = checkIsAolReadOnly(generalSectionVM?.sectionData);

    const initVM = () => {
        if (!selectedAolDataItem) return;
        const noonReport = selectedAolDataItem.noonReport;
        const vm: VM = {
            cargoOnBoard: roundTo0Decimal(getNumberValue(selectedAolDataItem?.aolItem?.cargoOnBoard, 0, true) ?? 0),
            ballast: getNumberValue(noonReport?.generalSection?.ballast, undefined, true),
            hours: getAolDataItemHours(selectedAolDataItem),
            sectionData: noonReport?.generalSection?.sectionData ?? createSectionData(NoonReportSectionEnum.General),
            idlOffsetHours: getNumberValue(noonReport?.generalSection?.idlOffsetHours, undefined, true),
            manualOffsetHours: getNumberValue(noonReport?.generalSection?.manualOffsetHours, undefined, true),
            notValidFieldsArray: [],
            hasInvalidFields: false,
            targetConsumption: vessel?.targetMainEngineConsumption,
            targetSpeed: vessel?.targetSpeed,
            noonReportDateTime: noonReport?.generalSection.noonReportDateTime,
            mrvSectionWarning: hasMrvData(noonReport),
            isInitialized: true,
        };

        updateTargetDeviationInVm(vm);
        setHasEmptyValues(false);
        setGeneralSectionVM({ ...vm });
    };

    const hasMrvData = (noonReport: ValidatedNoonReport | undefined): boolean => {
        return noonReport?.mrvSection?.mrvData?.some((o) => o.mrvDataRequired) ?? false;
    };

    const getAolDataItemHours = (aolDataItem: AolDataItem): number => {
        const noonReportHours = getNumberValue(aolDataItem.noonReport?.generalSection?.hours, 2, true) ?? 0;
        const manualOffsetHours = getNumberValue(aolDataItem.noonReport?.generalSection?.manualOffsetHours, 2, true) ?? 0;
        const idlOffsetHours = getNumberValue(aolDataItem.noonReport?.generalSection?.idlOffsetHours, 2, true) ?? 0;

        let totalReportHours = noonReportHours + manualOffsetHours + idlOffsetHours;
        if (totalReportHours === 0) {
            totalReportHours = roundTo2Decimals(aolDataItem.reportHours);
        }
        return totalReportHours;
    };

    const updateTargetDeviationInVm = (vm: VM) => {
        vm.targetDeviation = '';
        vm.targetDeviationDetails = [];
        vm.isTargetDeviationWarning = false;

        const totalMainEngineConsumption = selectedAolDataItem?.aolDataItemProperties?.totalMainEngineConsumption ?? 0;
        const reportHours = roundTo2Decimals(selectedAolDataItem?.reportHours ?? 0);

        if (totalMainEngineConsumption > 0 && reportHours > 0) {
            const mtpd = (totalMainEngineConsumption / reportHours) * 24;
            const target = vessel?.targetMainEngineConsumption ?? 0;
            const targetMargin = (vessel?.mainEngineConsumptionDeviationMargin ?? 0) * 100;
            if (target > 0) {
                const deviationFromTarget = mtpd - target;
                const deviationFromTargetInPct = (deviationFromTarget / target) * 100;
                if (Math.abs(deviationFromTargetInPct) > 10) {
                    vm.targetDeviation = truncateTo2Decimals(deviationFromTargetInPct) + '%';

                    vm.targetDeviationDetails.push('Cons. (mtpd) = (ME cons. HFO + ME cons. MGO) / hours * 24');
                    vm.targetDeviationDetails.push(
                        'Cons. (mtpd) = ' + truncateTo2Decimals(totalMainEngineConsumption) + ' / ' + reportHours + ' * 24'
                    );
                    vm.targetDeviationDetails.push('Cons. (mtpd) = ' + truncateTo2Decimals(mtpd));
                    vm.targetDeviationDetails.push('Deviation (mtpd) = Cons. (mtpd) - Target cons. (mtpd)');
                    vm.targetDeviationDetails.push('Deviation (mtpd) = ' + truncateTo2Decimals(mtpd) + ' - ' + truncateTo2Decimals(target));
                    vm.targetDeviationDetails.push('Deviation (mtpd) = ' + truncateTo2Decimals(deviationFromTarget));

                    vm.isTargetDeviationWarning = Math.abs(deviationFromTarget) > targetMargin;
                } else {
                    setTargetDeviationDetailsExpanded(false);
                }
                setGeneralSectionVM({ ...vm });
            }
        }
    };

    const showTargetConsumptionAndSpeed = (): boolean => {
        return (
            !(selectedAolDataItem?.aolDataItemProperties?.speedOrderHours === generalSectionVM.hours) &&
            !(selectedAolDataItem?.aolDataItemProperties?.nonTargetOrderHours === generalSectionVM.hours)
        );
    };

    const showTargetDeviations = (): boolean => {
        return selectedAolDataItem?.aolDataItemProperties?.consumptionOrderHours === generalSectionVM.hours;
    };

    const onInputChange = (changeObject: InputChangeData) => {
        const name = changeObject.event.target.name;
        const value = changeObject.event.target.value;
        generalSectionVM[name] = value ? +value : undefined;

        hasInvalidFields(generalSectionVM, name, changeObject.isValid);
        setGeneralSectionVM({ ...generalSectionVM });
        if (hasEmptyValues) setHasEmptyValues(false);
    };

    const save = () => {
        const ignoreList: string[] = ['noonReportDateTime'];

        if (hasMissingValues(generalSectionVM, ignoreList)) {
            setHasEmptyValues(true);
        } else {
            const data = new NoonReportGeneralSectionFactory().createBM(generalSectionVM);
            if (hasEmptyValues) setHasEmptyValues(false);

            if (!generalSectionVM.hasInvalidFields) {
                saveSection(data);
            }
        }
    };

    return (
        <>
            {selectedAolDataItem && (
                <GeneralSectionInputElementWrapper key={`${selectedAolDataItem.id}_general_section`}>
                    <OdfjellInputComponent
                        type={'duration'}
                        isReadOnly={true}
                        description={'Hours'}
                        value={generalSectionVM.hours}
                        onChange={onInputChange}
                    />
                    <OdfjellInputComponent
                        type={'number'}
                        isReadOnly={true}
                        description={'Cargo on board'}
                        value={generalSectionVM.cargoOnBoard}
                        onChange={onInputChange}
                        unit={'MT'}
                    />
                    <OdfjellInputComponent
                        type={'number'}
                        isReadOnly={isReadOnly}
                        description={'Ballast'}
                        min={0}
                        max={100000}
                        value={generalSectionVM.ballast}
                        name={'ballast'}
                        onChange={onInputChange}
                        unit={'MT'}
                    />
                    {selectedAolDataItem.reportType === NoonReportTypeEnum.Sea && (
                        <>
                            {showTargetConsumptionAndSpeed() &&
                                generalSectionVM?.targetConsumption !== undefined &&
                                generalSectionVM?.targetConsumption > 0 && (
                                    <OdfjellInputComponent
                                        type={'number'}
                                        isReadOnly={true}
                                        description={'Target consumption'}
                                        value={generalSectionVM.targetConsumption}
                                        unit="mtpd"
                                    />
                                )}
                            {showTargetConsumptionAndSpeed() && generalSectionVM?.targetSpeed !== undefined && generalSectionVM?.targetSpeed > 0 && (
                                <OdfjellInputComponent
                                    type={'number'}
                                    isReadOnly={true}
                                    description={'Expected speed'}
                                    value={generalSectionVM.targetSpeed}
                                    unit="knot"
                                />
                            )}
                            {showTargetDeviations() && generalSectionVM.targetDeviation && (
                                <div className="input-and-icon">
                                    <span className={generalSectionVM.isTargetDeviationWarning ? 'target-deviation-warning' : ''}>
                                        <OdfjellInputComponent
                                            type={'text'}
                                            isReadOnly={true}
                                            description={'Deviation from target'}
                                            value={generalSectionVM.targetDeviation}
                                        />
                                        <div
                                            className="target-deviation-details-button-wrapper"
                                            onMouseEnter={() => setTargetDeviationDetailsExpanded(true)}
                                            onMouseLeave={() => setTargetDeviationDetailsExpanded(false)}
                                        >
                                            <button className="target-deviation-details-button">
                                                <FontAwesomeIcon icon="info-circle" />
                                            </button>
                                        </div>
                                        <span
                                            className={
                                                'target-deviation-details ' +
                                                (targetDeviationDetailsExpanded
                                                    ? 'target-deviation-details-expanded'
                                                    : 'target-deviation-details-hidden')
                                            }
                                            key={`${selectedAolDataItem.id}_target_details`}
                                        >
                                            {generalSectionVM?.targetDeviationDetails?.map((o, i) => (
                                                <span className="target-deviation-detail" key={`${i}_target_detail`}>
                                                    {o}
                                                </span>
                                            ))}
                                        </span>
                                    </span>
                                </div>
                            )}
                        </>
                    )}
                    {generalSectionVM.mrvSectionWarning && (
                        <span className="mrv-data-warning">
                            The original AOL created in ORCA 7 contains an MRV section. Please use ORCA 7 or 8 to edit it.
                        </span>
                    )}
                </GeneralSectionInputElementWrapper>
            )}
        </>
    );
};

export default GeneralSectionComponent;
